import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import type { QuestionOption } from "../questionConfig";

interface SingleSelectProps {
  label: string;
  description?: string;
  options: QuestionOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  required?: boolean;
}

export function SingleSelect({
  label,
  description,
  options,
  value,
  onChange,
}: SingleSelectProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-[var(--dug-text-primary)]">{label}</h3>
        {description && (
          <p className="text-sm text-[var(--dug-text-secondary)] mt-1">{description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <motion.button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                relative p-4 rounded-xl border-2 text-left transition-all
                ${isSelected
                  ? "border-[var(--dug-coral)] bg-[var(--dug-coral)]/10"
                  : "border-[var(--dug-border)] hover:border-[var(--dug-coral)]/50 bg-[var(--dug-bg-surface)]"
                }
              `}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <span className={`
                    font-medium block
                    ${isSelected ? "text-[var(--dug-coral)]" : "text-[var(--dug-text-primary)]"}
                  `}>
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="text-sm text-[var(--dug-text-muted)] mt-0.5 block">
                      {option.description}
                    </span>
                  )}
                </div>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-5 h-5 rounded-full bg-[var(--dug-coral)] flex items-center justify-center flex-shrink-0"
                  >
                    <Check className="w-3 h-3 text-[var(--dug-text-inverse)]" />
                  </motion.div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
